import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../api';

/**
 * Attendee-facing page: shows the user's unique QR admission ticket for an event.
 * Route: /events/:eventId/ticket
 */
export default function MyTicketPage() {
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/checkin/events/${eventId}/my-ticket/`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load your ticket.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading your ticket…</div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-red-600">{error}</p>
        <button onClick={load} className="px-4 py-2 rounded bg-indigo-600 text-white">Retry</button>
      </div>
    );
  }

  const elig = data?.eligibility || {};
  const ticket = data?.ticket;

  // Not eligible yet — explain why.
  if (!data?.eligible) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow p-8 text-center">
          <h1 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Ticket not available yet</h1>
          {elig.reason === 'not_confirmed' && (
            <p className="text-gray-600 dark:text-gray-300">You need to be a confirmed guest of this event before a ticket can be issued.</p>
          )}
          {elig.reason === 'payment_incomplete' && (
            <div className="text-gray-600 dark:text-gray-300">
              <p>Your payment isn’t fully verified yet.</p>
              <p className="mt-2">Ticket price: <b>KES {elig.ticket_price?.toLocaleString()}</b></p>
              <p>Verified so far: <b>KES {elig.total_paid?.toLocaleString()}</b></p>
              <p className="text-red-600 font-medium mt-1">Balance due: KES {elig.balance?.toLocaleString()}</p>
            </div>
          )}
          <button onClick={load} className="mt-6 px-4 py-2 rounded bg-indigo-600 text-white">Refresh</button>
        </div>
      </div>
    );
  }

  const admitted = ticket?.status === 'checked_in';

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-sm w-full bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
        <div className={`px-6 py-4 text-white ${admitted ? 'bg-green-600' : 'bg-indigo-600'}`}>
          <p className="text-xs uppercase tracking-wide opacity-80">Admission ticket</p>
          <h1 className="text-lg font-semibold truncate">{ticket.event_title}</h1>
        </div>

        <div className="p-6 flex flex-col items-center">
          {admitted ? (
            <div className="text-center py-6">
              <div className="text-5xl mb-2">✅</div>
              <p className="text-green-700 font-semibold text-lg">Checked in</p>
              {ticket.checked_in_at && (
                <p className="text-gray-500 text-sm mt-1">
                  {new Date(ticket.checked_in_at).toLocaleString()}
                </p>
              )}
            </div>
          ) : (
            <div className="p-3 bg-white border rounded-xl">
              <QRCodeCanvas value={ticket.code} size={220} includeMargin level="M" />
            </div>
          )}

          <div className="mt-5 w-full text-center">
            <p className="font-medium text-gray-800 dark:text-gray-100">{ticket.attendee_name}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{ticket.attendee_email}</p>
            {ticket.seat_number && (
              <p className="mt-1 text-sm">Seat: <b>{ticket.seat_number}</b></p>
            )}
            <p className="mt-3 text-[10px] text-gray-400 break-all">#{ticket.code}</p>
          </div>

          {!admitted && (
            <p className="mt-4 text-xs text-center text-gray-500">
              Present this QR code at the entrance. Don’t share it — it admits one person, once.
            </p>
          )}
          <button onClick={load} className="mt-4 text-sm text-indigo-600">Refresh status</button>
          <Link to="/my-events" className="mt-2 text-xs text-gray-400">Back to my events</Link>
        </div>
      </div>
    </div>
  );
}
